import React, { createContext, useContext, ReactNode } from 'react';
import { IMapProvider, MapProviderType } from './IMapProvider';
import { LeafletMapProvider } from './LeafletMapProvider';
import { MAP_PROVIDER_CONFIG } from './mapProviderConfig';

interface MapContextType {
  provider: IMapProvider;
  providerType: MapProviderType;
}

const MapContext = createContext<MapContextType | undefined>(undefined);

interface MapProviderProps {
  children: ReactNode;
}

/**
 * MapProvider component - manages which map provider to use
 * The provider is configured globally in mapProviderConfig.ts
 * This allows easy switching between different map implementations
 */
export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  // Factory to create the appropriate map provider
  const createProvider = (type: MapProviderType): IMapProvider => {
    switch (type) {
      case MapProviderType.LEAFLET:
        return new LeafletMapProvider();
      case MapProviderType.GOOGLE_MAPS:
        // Future implementation
        throw new Error('Google Maps provider not yet implemented');
      case MapProviderType.MAPBOX:
        // Future implementation
        throw new Error('Mapbox provider not yet implemented');
      default:
        return new LeafletMapProvider();
    }
  };

  const providerType = MAP_PROVIDER_CONFIG.defaultProvider;
  const provider = createProvider(providerType);

  return (
    <MapContext.Provider value={{ provider, providerType }}>
      {children}
    </MapContext.Provider>
  );
};

/**
 * Hook to access the current map provider
 */
export const useMapProvider = (): MapContextType => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapProvider must be used within a MapProvider');
  }
  return context;
};